/**
 * Per-recipient color palette used to visually distinguish field placements
 * in the editor. Tailwind's color tokens give us tinted backgrounds plus a
 * stronger border + text color for legibility.
 *
 * Index lookup is wrapped (`% PALETTE.length`) so envelopes with more
 * recipients than palette entries still render — colors will repeat, which
 * is acceptable since real envelopes rarely cross a handful of recipients.
 */

export interface RecipientColor {
  /** Hex border color used on the field overlay frame. */
  border: string;
  /** Hex background tint behind the field. */
  background: string;
  /** Solid foreground/dot color for the legend swatch. */
  swatch: string;
  /** Tailwind class for the swatch — kept in lockstep with `swatch` for
   *  components that need a class instead of an inline style. */
  swatchClass: string;
}

const PALETTE: RecipientColor[] = [
  {
    border: '#2563eb',
    background: 'rgba(37, 99, 235, 0.18)',
    swatch: '#2563eb',
    swatchClass: 'bg-blue-600',
  },
  {
    border: '#16a34a',
    background: 'rgba(22, 163, 74, 0.18)',
    swatch: '#16a34a',
    swatchClass: 'bg-green-600',
  },
  {
    border: '#db2777',
    background: 'rgba(219, 39, 119, 0.18)',
    swatch: '#db2777',
    swatchClass: 'bg-pink-600',
  },
  {
    border: '#ea580c',
    background: 'rgba(234, 88, 12, 0.18)',
    swatch: '#ea580c',
    swatchClass: 'bg-orange-600',
  },
  {
    border: '#9333ea',
    background: 'rgba(147, 51, 234, 0.18)',
    swatch: '#9333ea',
    swatchClass: 'bg-purple-600',
  },
  {
    border: '#0891b2',
    background: 'rgba(8, 145, 178, 0.18)',
    swatch: '#0891b2',
    swatchClass: 'bg-cyan-600',
  },
  {
    border: '#ca8a04',
    background: 'rgba(202, 138, 4, 0.18)',
    swatch: '#ca8a04',
    swatchClass: 'bg-yellow-600',
  },
  {
    border: '#475569',
    background: 'rgba(71, 85, 105, 0.18)',
    swatch: '#475569',
    swatchClass: 'bg-slate-600',
  },
];

export function getRecipientColor(index: number): RecipientColor {
  if (index < 0) return PALETTE[0];
  return PALETTE[index % PALETTE.length];
}
